/**
 * MultiWalletSwitcher.jsx — Gestionnaire de wallets liés (mode authentifié).
 *
 * Fonctionnalités :
 *   - Liste tous les wallets liés (GET /auth/wallets via refreshLinkedWallets)
 *   - "Set as primary"   → POST /auth/wallet/switch  (wallet non-primaire uniquement)
 *   - "Retirer"          → DELETE /auth/wallet/:id   (avec confirmation DA native,
 *                                                     caché si un seul wallet)
 *   - "Ajouter un wallet" → openModal('link')
 *   - "Exporter mes clés" → dispatch OPEN_KEY_EXPORT, affiché si ≥1 wallet GENERATED
 *
 * Utilisé dans currentFlow = 'link' | 'export' (contexte authentifié).
 *
 * Règles DA :
 *   §20  — zéro littéral couleur hors theme.js
 *   §12 ter — zéro px en dur
 *   §18 ter — au moins 1 breakpoint responsive
 *   §12 undecies — casse grammaticale
 *   §12 terdecies — ZÉRO window.confirm/alert — confirmation via modal DA inline
 *   §14  — zéro MUI
 *   §8.7 — zéro Redux
 */

import React, { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  WalletBody,
  WalletFooter,
  WalletList,
  WalletListItem,
  WalletAddressSpan,
  WalletTypeBadge,
  PrimaryBtn,
  SecondaryBtn,
  DangerBtn,
  AlertBox,
  AlertText,
} from '../styles/EarniWalletModal.styles';
import {COLORS, FONTS, RADII, SHADOW, SPACE, media, FONT_SIZE, FONT_WEIGHT} from '../../ui/theme';
import { ACTION_TYPES }  from '../hooks/useWalletStateMachine';
import { switchWallet, deleteWallet, disownWallet } from '../services/WalletAuthService';
import { resetCustodialListingCache } from '../../../utils/functions/custodialListing';
import { shortAddress } from '../../../utils/format';

/* ─── Styled-components locaux ──────────────────────────────────────────────── */

/** Ligne de titre de section */
const SectionTitle = styled.h4`
  margin: 0 0 ${SPACE.sm};
  font-family: ${FONTS.head};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.muted};
  text-transform: none;
  letter-spacing: 0;
`;

/** Actions inline sur chaque wallet */
const WalletActions = styled.div`
  display: flex;
  gap: ${SPACE.xs};
  flex-shrink: 0;
  flex-wrap: wrap;

  ${media.sm`
    flex-direction: column;
  `}
`;

const SmallBtn = styled.button`
  border: 0.0625rem solid ${(p) => (p.$danger ? COLORS.danger : COLORS.line)};
  background: transparent;
  color: ${(p) => (p.$danger ? COLORS.danger : COLORS.ink)};
  border-radius: ${RADII.button};
  padding: ${SPACE.xs} ${SPACE.sm};
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.semibold};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$danger ? COLORS.dangerSoft : COLORS.subtle)};
    border-color: ${(p) => (p.$danger ? COLORS.dangerDeep : COLORS.accent)};
    color: ${(p) => (p.$danger ? COLORS.dangerDeep : COLORS.accent)};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/** Ligne "You own X collectibles" — centrée, accent bold (cf. preview dav2-wallet2-mywallet). */
const OwnLine = styled.p`
  margin: 0 0 ${SPACE.sm};
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: 0.86rem;
  color: ${COLORS.ink};
  text-align: center;

  b {
    color: ${COLORS.accent};
    font-weight: ${FONT_WEIGHT.extrabold};
  }
`;

/** Bouton "View my collectibles" — outline accent pleine largeur (cf. preview). */
const CollectBtn = styled.button`
  width: 100%;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.9rem;
  color: ${COLORS.accent};
  background: ${COLORS.white};
  border: 0.0938rem solid ${COLORS.accent45};
  border-radius: ${RADII.button};
  padding: 0.8em;
  cursor: pointer;
  margin-bottom: ${SPACE.md};
  transition: background 0.12s;

  &:hover {
    background: ${COLORS.accent14};
  }
`;

/** Bouton "Export recovery keys" — variante plus courte + marge sous, pour respirer
    avant "Link a wallet" (demande Jonathan 2026-06-10). N'altère pas le SecondaryBtn global. */
const ExportKeysBtn = styled(SecondaryBtn)`
  height: 2.1rem;
  font-size: 0.82rem;
  margin-bottom: ${SPACE.sm};
`;

/** Spinner minimaliste inline */
const InlineSpinner = styled.span`
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  border: 0.125rem solid ${COLORS.accentSoft};
  border-top-color: ${COLORS.accent};
  border-radius: ${RADII.circle};
  animation: spin 0.8s linear infinite;
  vertical-align: middle;

  @keyframes spin { to { transform: rotate(360deg); } }
`;

/** Modal de confirmation inline (§12 terdecies — ZÉRO window.confirm) */
const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${COLORS.overlayLight};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${SPACE.lg};
  font-family: ${FONTS.body};
`;

const ConfirmCard = styled.div`
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  padding: ${SPACE.xl};
  max-width: 24rem;
  width: 100%;
  box-shadow: ${SHADOW.card};
  display: flex;
  flex-direction: column;
  gap: ${SPACE.lg};

  ${media.sm`
    padding: ${SPACE.lg};
  `}
`;

const ConfirmText = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: 0.9375rem;
  color: ${COLORS.ink};
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: ${SPACE.sm};
  justify-content: flex-end;
  flex-wrap: wrap;
`;

/** Recovery custody — badge inline sur les wallets GENERATED (SPEC-2026-06-24 PR #4/#5). */
const RecoveryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE.xs};
  margin-left: ${SPACE.sm};
  padding: 0.125rem 0.5rem;
  border-radius: ${RADII.pill};
  font-family: ${FONTS.body};
  font-size: 0.75rem;
  font-weight: ${FONT_WEIGHT.bold};
  background: ${(p) => (p.$enabled ? COLORS.accentSoft : COLORS.line)};
  color: ${(p) => (p.$enabled ? COLORS.accent : COLORS.muted)};
`;

/** Texte d'alerte du disclaimer disown — rouge danger, sur fond clair (§20 token). */
const DangerNotice = styled.div`
  background: ${COLORS.dangerSoft};
  border-left: 0.25rem solid ${COLORS.danger};
  border-radius: ${RADII.input};
  padding: ${SPACE.md};
  font-family: ${FONTS.body};
  font-size: 0.875rem;
  color: ${COLORS.danger};
  line-height: 1.4;

  strong {
    font-weight: ${FONT_WEIGHT.bold};
  }
`;

/** Checkbox d'acknowledgement — case + label, inline. */
const AckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE.sm};
  cursor: pointer;
  font-family: ${FONTS.body};
  font-size: 0.875rem;
  color: ${COLORS.ink};

  input[type='checkbox'] {
    margin-top: 0.125rem;
    accent-color: ${COLORS.accent};
    cursor: pointer;
  }
`;

/* ─── Icônes de type wallet ─────────────────────────────────────────────────── */
const WALLET_TYPE_LABELS = {
  GENERATED:    'Generated',
  METAMASK:     'MetaMask',
  COINBASE:     'Coinbase',
  TRUSTWALLET:  'Trust Wallet',
  BINANCE:      'Binance',
  WALLETCONNECT:'WalletConnect',
  LEDGER:       'Ledger',
};

/* ─── Composant ─────────────────────────────────────────────────────────────── */

/**
 * MultiWalletSwitcher
 *
 * @param {object}   props
 * @param {function} props.dispatch             - dispatch de useWalletStateMachine
 * @param {string}   props.apiBaseUrl           - URL de base de l'API
 * @param {Array}    props.linkedWallets         - liste courante des wallets liés
 * @param {function} props.refreshLinkedWallets  - recharge depuis /auth/wallets
 * @param {function} props.openModal             - ouvre la modal avec un flow donné
 */
export function MultiWalletSwitcher({
  dispatch,
  apiBaseUrl,
  linkedWallets,
  refreshLinkedWallets,
  openModal,
  /* Compteur de collectibles possédés + navigation (fournis par le parent §8.7).
     null → bloc masqué (consommateur sans la donnée). */
  collectiblesCount = null,
  onViewCollectibles,
}) {
  /* ── Chargement initial ───────────────────────────────────────────────────── */
  useEffect(() => {
    refreshLinkedWallets();
  }, [refreshLinkedWallets]);

  /* ── États d'action ──────────────────────────────────────────────────────── */
  const [switchingId,   setSwitchingId]   = useState(null);
  const [deletingId,    setDeletingId]    = useState(null);
  const [actionError,   setActionError]   = useState(null);

  /* ── Confirmation de suppression (modal DA inline) ──────────────────────── */
  const [confirmDeleteId,   setConfirmDeleteId]   = useState(null);
  const confirmDeleteWallet = linkedWallets.find((w) => w.id === confirmDeleteId);

  /* ── Set as Primary ──────────────────────────────────────────────────────── */
  const handleSetPrimary = useCallback(async (walletId) => {
    setActionError(null);
    setSwitchingId(walletId);
    try {
      await switchWallet(apiBaseUrl, { walletId });
      // Le wallet primaire change, la capacite custodialListing peut basculer
      // (GENERATED vs METAMASK). Invalider le cache pour forcer une re-resolution
      // au prochain listing. CHEMIN 1 (gasless custodial listing, SPEC v3).
      resetCustodialListingCache();
      await refreshLinkedWallets();
    } catch (err) {
      setActionError(err?.message ?? 'Unable to change the primary wallet.');
    } finally {
      setSwitchingId(null);
    }
  }, [apiBaseUrl, refreshLinkedWallets]);

  /* ── Supprimer (ouvre la confirmation) ──────────────────────────────────── */
  const handleDeleteRequest = useCallback((walletId) => {
    setActionError(null);
    setConfirmDeleteId(walletId);
  }, []);

  /* ── Confirmer la suppression ────────────────────────────────────────────── */
  const handleDeleteConfirm = useCallback(async () => {
    if (!confirmDeleteId) return;
    const wid = confirmDeleteId;
    setConfirmDeleteId(null);
    setDeletingId(wid);
    try {
      await deleteWallet(apiBaseUrl, wid);
      await refreshLinkedWallets();
    } catch (err) {
      setActionError(err?.message ?? 'Unable to remove this wallet.');
    } finally {
      setDeletingId(null);
    }
  }, [confirmDeleteId, apiBaseUrl, refreshLinkedWallets]);

  /* ── Annuler la confirmation ─────────────────────────────────────────────── */
  const handleDeleteCancel = useCallback(() => {
    setConfirmDeleteId(null);
  }, []);

  /* ── Disown recovery (SPEC-2026-06-24-wallet-kek-migration.v1 PR #4) ─────── */
  const [confirmDisownId,  setConfirmDisownId]  = useState(null);
  const [disowningId,      setDisowningId]      = useState(null);
  const [disownAck,        setDisownAck]        = useState(false);
  const confirmDisownWallet = linkedWallets.find((w) => w.id === confirmDisownId);

  const handleDisownRequest = useCallback((walletId) => {
    setActionError(null);
    setDisownAck(false);
    setConfirmDisownId(walletId);
  }, []);

  const handleDisownCancel = useCallback(() => {
    setConfirmDisownId(null);
    setDisownAck(false);
  }, []);

  const handleDisownConfirm = useCallback(async () => {
    if (!confirmDisownId || !disownAck) return;
    const wid = confirmDisownId;
    setConfirmDisownId(null);
    setDisownAck(false);
    setDisowningId(wid);
    try {
      await disownWallet(apiBaseUrl, { walletId: wid, confirmed: true });
      await refreshLinkedWallets();
    } catch (err) {
      setActionError(err?.message ?? 'Unable to revoke recovery.');
    } finally {
      setDisowningId(null);
    }
  }, [confirmDisownId, disownAck, apiBaseUrl, refreshLinkedWallets]);

  /* ── Export de clé — uniquement si ≥1 wallet GENERATED ─────────────────── */
  const hasGeneratedWallet = linkedWallets.some((w) => w.walletType === 'GENERATED');

  const handleExportKeys = useCallback(() => {
    dispatch({ type: ACTION_TYPES.OPEN_KEY_EXPORT });
  }, [dispatch]);

  /* ── Rendu ──────────────────────────────────────────────────────────────── */
  return (
    <>
      <WalletBody>
        {/* Bloc collectibles possédés (cf. preview dav2-wallet2-mywallet) — masqué si non fourni */}
        {collectiblesCount != null && (
          <>
            <OwnLine>
              You own <b>{collectiblesCount}</b>{' '}
              {collectiblesCount === 1 ? 'collectible' : 'collectibles'}
            </OwnLine>
            {typeof onViewCollectibles === 'function' && (
              <CollectBtn
                type="button"
                onClick={onViewCollectibles}
                aria-label="View my collectibles"
              >
                View my collectibles
              </CollectBtn>
            )}
          </>
        )}

        <SectionTitle>My wallets ({linkedWallets.length})</SectionTitle>

        {/* Message d'erreur d'action */}
        {actionError && (
          <AlertBox $type="error" role="alert">
            <AlertText $type="error">{actionError}</AlertText>
          </AlertBox>
        )}

        {/* Liste des wallets */}
        {linkedWallets.length === 0 ? (
          <AlertBox $type="warning" role="status">
            <AlertText $type="warning">No linked wallet. Add one below.</AlertText>
          </AlertBox>
        ) : (
          <WalletList>
            {linkedWallets.map((wallet) => {
              const shortAddr = shortAddress(wallet.address);
              const typeLabel = WALLET_TYPE_LABELS[wallet.walletType] ?? wallet.walletType;
              const isBusy    = switchingId === wallet.id || deletingId === wallet.id;
              const isOnly    = linkedWallets.length === 1;

              return (
                <WalletListItem
                  key={wallet.id}
                  $isPrimary={wallet.isPrimary}
                  aria-label={`Wallet ${typeLabel} ${shortAddr}${wallet.isPrimary ? ', primary wallet' : ''}`}
                >
                  {/* Infos wallet */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <WalletTypeBadge $isPrimary={wallet.isPrimary}>
                      {wallet.isPrimary ? '★ Primary' : typeLabel}
                    </WalletTypeBadge>
                    <WalletAddressSpan title={wallet.address}>
                      {shortAddr}
                    </WalletAddressSpan>
                    {/* SPEC-2026-06-24-wallet-kek-migration.v1 PR #5 : recovery custody badge.
                        Affiché uniquement sur wallets GENERATED. Magenta si activé, gris si révoqué. */}
                    {wallet.walletType === 'GENERATED' && (
                      <RecoveryBadge $enabled={!!wallet.hasRecovery}>
                        {wallet.hasRecovery
                          ? 'Recovery enabled by Earniverse'
                          : 'Self-custody only'}
                      </RecoveryBadge>
                    )}
                  </div>

                  {/* Actions */}
                  <WalletActions>
                    {isBusy ? (
                      <InlineSpinner aria-label="Action in progress…" />
                    ) : (
                      <>
                        {/* Set as Primary — affiché seulement si non-primaire */}
                        {!wallet.isPrimary && (
                          <SmallBtn
                            type="button"
                            onClick={() => handleSetPrimary(wallet.id)}
                            disabled={isBusy}
                            aria-label={`Set ${shortAddr} as primary wallet`}
                          >
                            Set as primary
                          </SmallBtn>
                        )}
                        {/* Retirer — caché si un seul wallet */}
                        {!isOnly && !wallet.isPrimary && (
                          <SmallBtn
                            $danger
                            type="button"
                            onClick={() => handleDeleteRequest(wallet.id)}
                            disabled={isBusy}
                            aria-label={`Remove wallet ${shortAddr}`}
                          >
                            Remove
                          </SmallBtn>
                        )}
                        {/* SPEC-2026-06-24-wallet-kek-migration.v1 PR #5 : Revoke recovery
                            uniquement si wallet GENERATED + recovery actuellement actif. */}
                        {wallet.walletType === 'GENERATED' && wallet.hasRecovery && (
                          <SmallBtn
                            $danger
                            type="button"
                            onClick={() => handleDisownRequest(wallet.id)}
                            disabled={isBusy || disowningId === wallet.id}
                            aria-label={`Revoke recovery for wallet ${shortAddr}`}
                          >
                            Revoke recovery
                          </SmallBtn>
                        )}
                      </>
                    )}
                  </WalletActions>
                </WalletListItem>
              );
            })}
          </WalletList>
        )}
      </WalletBody>

      {/* Footer — actions globales */}
      <WalletFooter>
        {/* Exporter les clés — uniquement si ≥1 wallet GENERATED */}
        {hasGeneratedWallet && (
          <ExportKeysBtn
            type="button"
            onClick={handleExportKeys}
            aria-label="Export recovery keys"
          >
            Export recovery keys
          </ExportKeysBtn>
        )}

        {/* Ajouter un wallet */}
        <PrimaryBtn
          type="button"
          onClick={() => openModal('link')}
          aria-label="Link a wallet"
        >
          Link a wallet
        </PrimaryBtn>
      </WalletFooter>

      {/* ── Modal disown recovery (§12 terdecies — ZÉRO window.confirm, SPEC PR #5) ── */}
      {confirmDisownId && confirmDisownWallet && (
        <ConfirmOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Confirm recovery revocation"
          onClick={(e) => { if (e.target === e.currentTarget) handleDisownCancel(); }}
        >
          <ConfirmCard>
            <ConfirmText>
              <strong>Revoke recovery for wallet{' '}
                {shortAddress(confirmDisownWallet.address)}?
              </strong>
            </ConfirmText>
            <DangerNotice>
              <strong>Warning:</strong> after revocation, Earniverse will <strong>no longer hold</strong>{' '}
              the recovery key for this wallet. If you ever lose your password,{' '}
              <strong>your wallet cannot be recovered</strong> and the funds inside will be lost.
              This action is permanent.
            </DangerNotice>
            <AckRow>
              <input
                type="checkbox"
                checked={disownAck}
                onChange={(e) => setDisownAck(e.target.checked)}
                aria-label="I confirm I have saved my recovery keys"
              />
              <span>
                I have already exported and securely saved my recovery keys{' '}
                (mnemonic phrase or private key).
              </span>
            </AckRow>
            <ConfirmActions>
              <SecondaryBtn
                type="button"
                onClick={handleDisownCancel}
                style={{ width: 'auto', minWidth: '7rem' }}
              >
                Cancel
              </SecondaryBtn>
              <DangerBtn
                type="button"
                onClick={handleDisownConfirm}
                disabled={!disownAck}
                aria-disabled={!disownAck}
              >
                Revoke now
              </DangerBtn>
            </ConfirmActions>
          </ConfirmCard>
        </ConfirmOverlay>
      )}

      {/* ── Modal de confirmation DA inline (§12 terdecies — ZÉRO window.confirm) ── */}
      {confirmDeleteId && confirmDeleteWallet && (
        <ConfirmOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Confirm wallet removal"
          onClick={(e) => { if (e.target === e.currentTarget) handleDeleteCancel(); }}
        >
          <ConfirmCard>
            <ConfirmText>
              Remove wallet{' '}
              <strong>
                {shortAddress(confirmDeleteWallet.address)}
              </strong>{' '}
              ({WALLET_TYPE_LABELS[confirmDeleteWallet.walletType] ?? confirmDeleteWallet.walletType})?
              <br />
              This action is irreversible.
            </ConfirmText>
            <ConfirmActions>
              <SecondaryBtn
                type="button"
                onClick={handleDeleteCancel}
                style={{ width: 'auto', minWidth: '7rem' }}
              >
                Cancel
              </SecondaryBtn>
              <DangerBtn
                type="button"
                onClick={handleDeleteConfirm}
              >
                Remove permanently
              </DangerBtn>
            </ConfirmActions>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
    </>
  );
}

export default MultiWalletSwitcher;
